import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
	'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isElementVisible = (element) =>
	Boolean(element.getClientRects().length) && !element.closest('[hidden]');

const getFocusableElements = (root) =>
	Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isElementVisible);

const getInitialFocusTarget = (dialog) => {
	const autoFocusEl = dialog.querySelector('[autofocus]');
	if (autoFocusEl && !autoFocusEl.disabled && isElementVisible(autoFocusEl)) {
		return autoFocusEl;
	}

	const input = dialog.querySelector('input:not([disabled]), textarea:not([disabled])');
	if (input && isElementVisible(input)) return input;

	return getFocusableElements(dialog)[0] || null;
};

const hideBackgroundFromA11yTree = (dialog) => {
	const hidden = [];
	const main = document.getElementById('main-content');
	const header = document.querySelector('header');

	[main, header].forEach((element) => {
		if (element && !element.contains(dialog)) {
			hidden.push([element, element.getAttribute('aria-hidden')]);
			element.setAttribute('aria-hidden', 'true');
		}
	});

	return hidden;
};

const restoreBackgroundA11yTree = (hidden) => {
	hidden.forEach(([element, previousValue]) => {
		if (previousValue === null) {
			element.removeAttribute('aria-hidden');
		} else {
			element.setAttribute('aria-hidden', previousValue);
		}
	});
};

const useDialogA11y = (isOpen, onClose) => {
	const dialogRef = useRef(null);
	const previousFocusRef = useRef(null);
	const onCloseRef = useRef(onClose);

	onCloseRef.current = onClose;

	useEffect(() => {
		if (!isOpen) return undefined;

		previousFocusRef.current = document.activeElement;

		let active = true;
		let rafId = 0;
		let hiddenBackground = [];
		let removeListeners = () => {};

		const attach = () => {
			if (!active) return;

			const dialog = dialogRef.current;
			if (!dialog) {
				rafId = requestAnimationFrame(attach);
				return;
			}

			const getFocusable = () => getFocusableElements(dialog);

			const focusInitial = () => {
				const initialFocus = getInitialFocusTarget(dialog);
				if (initialFocus) {
					initialFocus.focus({ preventScroll: true });
				}
			};

			const handleKeyDown = (event) => {
				if (event.key === 'Escape') {
					event.preventDefault();
					onCloseRef.current();
					return;
				}

				if (event.key !== 'Tab') return;

				const focusable = getFocusable();
				if (focusable.length === 0) {
					event.preventDefault();
					return;
				}

				const first = focusable[0];
				const last = focusable[focusable.length - 1];
				const current = document.activeElement;

				if (!dialog.contains(current)) {
					event.preventDefault();
					(event.shiftKey ? last : first).focus({ preventScroll: true });
					return;
				}

				if (event.shiftKey && current === first) {
					event.preventDefault();
					last.focus({ preventScroll: true });
				} else if (!event.shiftKey && current === last) {
					event.preventDefault();
					first.focus({ preventScroll: true });
				}
			};

			const handleFocusIn = (event) => {
				if (!active || dialog.contains(event.target)) return;

				const focusable = getFocusable();
				if (focusable.length === 0) return;

				event.stopPropagation();
				focusable[0].focus({ preventScroll: true });
			};

			hiddenBackground = hideBackgroundFromA11yTree(dialog);

			document.addEventListener('keydown', handleKeyDown);
			document.addEventListener('focusin', handleFocusIn, true);

			focusInitial();
			rafId = requestAnimationFrame(focusInitial);

			removeListeners = () => {
				document.removeEventListener('keydown', handleKeyDown);
				document.removeEventListener('focusin', handleFocusIn, true);
				restoreBackgroundA11yTree(hiddenBackground);
			};
		};

		attach();

		return () => {
			active = false;
			cancelAnimationFrame(rafId);
			removeListeners();
			previousFocusRef.current?.focus?.({ preventScroll: true });
		};
	}, [isOpen]);

	return dialogRef;
};

export default useDialogA11y;
